import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import {
  createTextPlaceholderKey,
  fetchScenarioTextPlaceholders,
  TextPlaceholderKey,
  TextPlaceholderScenarioEntry,
  upsertScenarioTextPlaceholderValue,
} from './text-placeholder-api';

interface TextPlaceholderContextValue {
  keys: TextPlaceholderKey[];
  entries: TextPlaceholderScenarioEntry[];
  values: Record<string, string>;
  loading: boolean;
  refresh: () => Promise<void>;
  upsertPlaceholder: (key: string, value: string, label?: string) => Promise<void>;
}

const TextPlaceholderContext = createContext<TextPlaceholderContextValue | null>(null);

interface TextPlaceholderProviderProps {
  scenarioId: string;
  children: React.ReactNode;
}

export function TextPlaceholderProvider({ scenarioId, children }: TextPlaceholderProviderProps) {
  const { user } = useAuth();
  const [entries, setEntries] = useState<TextPlaceholderScenarioEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || !scenarioId) {
      setEntries([]);
      return;
    }
    setLoading(true);
    try {
      const list = await fetchScenarioTextPlaceholders(scenarioId);
      setEntries(list);
    } catch (err) {
      console.error(err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [user, scenarioId]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh]);

  const upsertPlaceholder = useCallback(
    async (key: string, value: string, label?: string) => {
      if (!user || !scenarioId) {
        throw new Error('Not authenticated');
      }
      const trimmedKey = key.trim();
      const trimmedValue = value.trim();
      if (!trimmedKey || !trimmedValue) {
        throw new Error('Key and value are required');
      }

      const existing = entries.find((entry) => entry.key === trimmedKey);
      if (!existing) {
        await createTextPlaceholderKey({
          key: trimmedKey,
          label: label?.trim() || undefined,
        });
      } else if (label?.trim()) {
        await createTextPlaceholderKey({
          key: trimmedKey,
          label: label.trim(),
        });
      }

      await upsertScenarioTextPlaceholderValue(scenarioId, trimmedKey, trimmedValue);
      await refresh();
    },
    [user, scenarioId, entries, refresh]
  );

  const values = useMemo(() => {
    const map: Record<string, string> = {};
    for (const entry of entries) {
      if (entry.value != null) {
        map[entry.key] = entry.value;
      }
    }
    return map;
  }, [entries]);

  const keys = useMemo(
    () =>
      entries.map(({ key, label, description, created_at }) => ({
        key,
        label,
        description,
        created_at,
      })),
    [entries]
  );

  const value = useMemo(
    () => ({
      keys,
      entries,
      values,
      loading,
      refresh,
      upsertPlaceholder,
    }),
    [keys, entries, values, loading, refresh, upsertPlaceholder]
  );

  return (
    <TextPlaceholderContext.Provider value={value}>
      {children}
    </TextPlaceholderContext.Provider>
  );
}

export function useTextPlaceholders(): TextPlaceholderContextValue | null {
  return useContext(TextPlaceholderContext);
}

export function useTextPlaceholdersRequired(): TextPlaceholderContextValue {
  const ctx = useContext(TextPlaceholderContext);
  if (!ctx) {
    throw new Error('useTextPlaceholdersRequired must be used within TextPlaceholderProvider');
  }
  return ctx;
}
